import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackClick, initializeVisitorTracking } from '../../utils/analytics';

/**
 * Global click tracker component
 * Tracks all clicks on interactive elements throughout the app
 * Also handles visitor tracking initialization on app load
 */
const AnalyticsTracker = () => {
  const location = useLocation();

  // Initialize visitor tracking once when component mounts
  useEffect(() => {
    initializeVisitorTracking();
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      // Get the clicked element
      const element = e.target;
      const target = e.currentTarget;

      // Skip tracking if clicking on analytics elements or if it's a programmatic click
      if (element.closest('[data-no-track]')) {
        return;
      }

      // Determine element type and extract relevant data
      let elementType = 'unknown';
      let elementId = '';
      let elementClass = '';
      let elementText = '';
      let href = '';

      // Check if it's a link
      if (element.tagName === 'A' || element.closest('a')) {
        const link = element.tagName === 'A' ? element : element.closest('a');
        elementType = 'link';
        elementId = link.id || '';
        elementClass = link.className || '';
        elementText = link.textContent?.trim().substring(0, 100) || '';
        href = link.href || '';
      }
      // Check if it's a button
      else if (element.tagName === 'BUTTON' || element.closest('button')) {
        const button = element.tagName === 'BUTTON' ? element : element.closest('button');
        elementType = 'button';
        elementId = button.id || '';
        elementClass = button.className || '';
        elementText = button.textContent?.trim().substring(0, 100) || '';
      }
      // Check if it's an input/select
      else if (['INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName)) {
        elementType = element.tagName.toLowerCase();
        elementId = element.id || '';
        elementClass = element.className || '';
      }
      // Check for FontAwesome icons 
      else if (element.closest('svg') || element.closest('.fa')) {
        const iconParent = element.closest('[class*="icon"]') || element.closest('a') || element.closest('button');
        if (iconParent) {
          elementType = 'icon';
          elementId = iconParent.id || '';
          elementClass = iconParent.className || '';
          elementText = iconParent.getAttribute('aria-label') || iconParent.title || '';
        }
      }
      // Check for NavLink (React Router)
      else if (element.closest('a[class*="nav"]') || element.closest('[class*="link"]')) {
        const navElement = element.closest('a') || element;
        elementType = 'navigation';
        elementId = navElement.id || '';
        elementClass = navElement.className || '';
        elementText = navElement.textContent?.trim().substring(0, 100) || '';
        href = navElement.href || '';
      }
      // Generic interactive element
      else if (element.onclick || element.getAttribute('role') === 'button') {
        elementType = 'interactive';
        elementId = element.id || '';
        elementClass = element.className || '';
        elementText = element.textContent?.trim().substring(0, 100) || '';
      }

      // Only track if we identified a meaningful element
      if (elementType !== 'unknown') {
        trackClick({
          elementType,
          elementId,
          elementClass,
          elementText,
          href,
          pagePath: location.pathname,
          clickX: e.clientX,
          clickY: e.clientY,
        });
      }
    };

    // Add click listener to document
    document.addEventListener('click', handleClick, true); // Use capture phase

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [location.pathname]);

  // This component doesn't render anything
  return null;
};

export default AnalyticsTracker;

